import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Define the shape of user settings
export interface UserSettings {
  language: string;
  system_of_units: string;
  theme: string;
  interested_products: string[] | null;
  registration_mail_sent: boolean | null;
}

// Define initial/default settings
const defaultSettings: UserSettings = {
  language: 'en',
  system_of_units: 'SI',
  theme: 'system', // Options: 'light', 'dark', 'system'
  interested_products: null,
  registration_mail_sent: null,
};

// Define the context shape
interface UserSettingsContextType {
  settings: UserSettings;
  loading: boolean;
  error: string | null;
  updateSettings: (newSettings: Partial<UserSettings>) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

// Create the context
const UserSettingsContext = createContext<UserSettingsContextType | undefined>(undefined);

// Provider component
export const UserSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const settingsFetchInProgress = useRef<boolean>(false);
  const lastSettingsFetch = useRef<number>(0);
  const settingsRef = useRef<UserSettings>(defaultSettings);
  const userEmailRef = useRef<string | undefined>(undefined);

  // Keep refs in sync with state
  useEffect(() => {
    settingsRef.current = settings;
    userEmailRef.current = user?.email;
  }, [settings, user?.email]);

  const fetchSettings = useCallback(async () => {
    const currentUserEmail = userEmailRef.current;
    if (!currentUserEmail) {
      return;
    }
    
    const now = Date.now();
    if (settingsFetchInProgress.current || (lastSettingsFetch.current && now - lastSettingsFetch.current < 1000)) {
      return;
    }
    
    settingsFetchInProgress.current = true;
    lastSettingsFetch.current = now;
    setLoading(true);
    
    try {
      const response = await api.get(`/users/settings?email=${encodeURIComponent(currentUserEmail)}`);
      const newSettings = {
        ...defaultSettings,
        ...response.data,
      };
      
      setSettings(newSettings);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
      settingsFetchInProgress.current = false;
    }
  }, []);

  const updateSettings = useCallback(async (newSettings: Partial<UserSettings>) => {
    const currentUserEmail = userEmailRef.current;
    if (!currentUserEmail) return;
    
    setLoading(true);
    try {
      // First update our local state
      const updatedSettings = { ...settingsRef.current, ...newSettings };
      setSettings(updatedSettings);
      
      // Then send to the API
      await api.put(`/users/settings?email=${encodeURIComponent(currentUserEmail)}`, updatedSettings);
      
      setError(null);
    } catch (err) {
      console.error('Failed to update user settings:', err);
      setError('Failed to save settings');
      // Revert to previous settings if there's an error
      await fetchSettings();
    } finally {
      setLoading(false);
    }
  }, [fetchSettings]);

  // Simple refresh function to get latest settings from API
  const refreshSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);

  // Fetch settings on initial load or when user changes
  useEffect(() => {
    if (user?.email) {
      // Update the ref immediately
      userEmailRef.current = user.email;
      // Fetch settings for the new user
      fetchSettings();
    } else {
      // Reset to defaults if no user
      setSettings(defaultSettings);
      setLoading(false);
    }
  }, [user?.email, fetchSettings]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(() => ({
    settings,
    loading,
    error,
    updateSettings,
    refreshSettings,
  }), [settings, loading, error, updateSettings, refreshSettings]);

  return (
    <UserSettingsContext.Provider value={contextValue}>
      {children}
    </UserSettingsContext.Provider>
  );
};

// Custom hook for using the context
export const useUserSettings = () => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsProvider');
  }
  return context;
};

export default UserSettingsContext;